import React, { useCallback, useEffect, useRef, useState } from "react";
import type { MatchConfig } from "../types";

import CoreGameFactory from "core/src/game-factory.js";
import clone from "core/src/core/util/clone.js";

import GameCanvas from "../../game-canvas/GameCanvas";
import LocalGameHandler from "../local-game-handler.js";
import * as windowFocusHandler from "../window-focus-handler.js";

const coreGameFactory = CoreGameFactory();

interface Props {
  matchConfig: MatchConfig;
  size?: any;
  width?: number;
  height?: number;
  children?: React.ReactNode;
}

// A non-interactive, looping preview of a match (bots only), used in lobby cards
// and the local-setup preview. GameCanvas handles per-frame drawing; this just owns
// the local game's lifecycle (create, recreate on config change / game over, and
// pause/resume with window focus).
export default function GamePreview({ matchConfig, size, width, height, children }: Props) {
  const [localGame, setLocalGame] = useState<any>(null);
  const [failed, setFailed] = useState(false);
  const localGameRef = useRef<any>(null);
  const matchConfigRef = useRef(matchConfig);
  matchConfigRef.current = matchConfig;

  const createGame = useCallback(() => {
    const cfg = matchConfigRef.current;
    try {
      const botPlayers = cfg.players.map((player: any) => clone(player));
      botPlayers.forEach((p: any) => (p.type = "bot"));
      const game = coreGameFactory.create({
        seed: Math.floor(Math.random() * 100000),
        map: cfg.map,
        players: botPlayers,
        // Forward the power-up rules so a lobby card's preview spawns the same
        // power-ups its playstyle will (undefined falls back to engine defaults,
        // so the plain local-setup preview is unchanged).
        disabledPowerUps: (cfg as any).disabledPowerUps,
        powerUpSpawnChance: (cfg as any).powerUpSpawnChance,
        // Decorative preview: run the bots on the cheap AI profile (short
        // lookahead, throttled replanning) so the start screen stays light.
        cheapAi: true,
      });
      if (localGameRef.current) {
        localGameRef.current.stop();
      }
      const lg = LocalGameHandler({
        game,
        onGameOver: () => createGame(),
        // Decorative preview: step the sim at ~40fps (30 read as choppy on
        // phones) to keep motion smooth while still well under a full 60fps cost.
        maxFps: 40,
      });
      if (cfg.players.length > 1) {
        lg.start();
      }
      localGameRef.current = lg;
      setFailed(false);
      setLocalGame(lg);
    } catch (e) {
      // Core throws if it can't place every worm (too many for this arena). A
      // preview is decorative, so degrade to a blank tile instead of taking the
      // whole app down with it.
      if (localGameRef.current) {
        localGameRef.current.stop();
        localGameRef.current = null;
      }
      setLocalGame(null);
      setFailed(true);
    }
  }, []);

  // Mount: focus/blur pause-resume + create the first game; stop on unmount.
  useEffect(() => {
    function onFocus() {
      localGameRef.current?.resume();
    }
    function onBlur() {
      localGameRef.current?.pause();
    }
    windowFocusHandler.on("focus", onFocus);
    windowFocusHandler.on("blur", onBlur);
    createGame();
    return () => {
      windowFocusHandler.off("focus", onFocus);
      windowFocusHandler.off("blur", onBlur);
      localGameRef.current?.stop();
    };
  }, [createGame]);

  // Recreate when the map, player count, cosmetics, OR power-up rules change (skips
  // the initial mount). Lobby previews get their playstyle after the first counts
  // arrive, which changes disabledPowerUps / powerUpSpawnChance but not the map,
  // so those must be part of the signature or the preview keeps spawning the
  // wrong power-ups. Player colours/head styles are in the signature because
  // GameCanvas no longer rebuilds its renderers on every re-render (that was the
  // freeze-lag bug): a colour picked in the local-setup roster now reaches the
  // preview by recreating its game, not by leaning on incidental rebuilds.
  const cfgSig =
    matchConfig.map.name +
    "|" + matchConfig.players.length +
    "|" + matchConfig.players.map((p: any) => p.colorId + ":" + (p.headStyle ?? "")).join(",") +
    "|" + ((matchConfig as any).powerUpSpawnChance ?? "") +
    "|" + (((matchConfig as any).disabledPowerUps || []).join(","));
  const prevSigRef = useRef(cfgSig);
  useEffect(() => {
    if (prevSigRef.current !== cfgSig) {
      prevSigRef.current = cfgSig;
      createGame();
    }
  }, [cfgSig, createGame]);

  if (failed || !localGame) {
    // No game to draw (placement failed, or not created yet). Render nothing -
    // children are cloned with gameState in the normal path, so rendering them
    // here without one would crash (e.g. Overlay reads gameState.map).
    return null;
  }
  return (
    <GameCanvas
      config={{ size, width, height, preview: true }}
      gameState={localGame.gameState}
      players={matchConfig.players}
    >
      {React.Children.map(children, (child) =>
        React.isValidElement(child)
          ? React.cloneElement(child as React.ReactElement<any>, { gameState: localGame.gameState })
          : child
      )}
    </GameCanvas>
  );
}
